import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Space, Card, message } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { createProjectMilestonePlotApi } from '@/services';
import { useSearchParams } from 'react-router-dom';


interface IEditProps {
    visible?: boolean,
    projectID: string | number,
    plots?: ProjectMilestonePlot[]
    onUpdate: () => void,
    onClose: () => void
}

export const PlotEditPopup: React.FC<IEditProps> = (props) => {
    const { visible, projectID, plots, onUpdate, onClose } = props;
    const [plotList, setPlotList] = useState<Req.ProjectPlot[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalApi, modalContextHolder] = Modal.useModal();
    const [searchParams] = useSearchParams();
    const invoiceToken = searchParams.get('token') || '';
    // 初始化地块列表
    useEffect(() => {
        if (visible) {
            if (plots && plots.length > 0) {
                setPlotList(plots.map(plot => ({ID: plot.ID, plotName: plot.plotName, percentage: 1 / plots.length })));
            } else {
                setPlotList([{ plotName: '', percentage: 0.5 }, { plotName: '', percentage: 0.5 }]);
            }
        }
    }, [visible, plots]);

    const onPercentageChange = (index: number, value: number) => {
        // 验证占比是否在0-1之间
        if (value < 0 || value > 1) {
            message.error('占比必须在0-1之间');
            return;
        }
        const newList = [...plotList];
        newList[index] = { ...newList[index], percentage: value };
        setPlotList(newList);
    };

    // 处理地块名称变化
    const handlePlotNameChange = (index: number, value: string) => {
        const newList = [...plotList];
        newList[index] = { ...newList[index], plotName: value };
        setPlotList(newList);
    };

    // 添加新地块
    const addPlot = () => {
        setPlotList([...plotList, { plotName: '', percentage: 0 }]);
    };

    // 删除地块
    const removePlot = (index: number) => {
        const newList = [...plotList];
        newList.splice(index, 1);
        setPlotList(newList);
    };

    const onSaveData = async () => {
        try {
            setLoading(true);
            await createProjectMilestonePlotApi(projectID, plotList, invoiceToken);

            message.success('地块保存成功');
            onUpdate();
            onClose();
            setLoading(false);
        } catch (error: any) {
            message.error(error.message || '保存地块失败，请重试');
            setLoading(false);
        }
    }

    // 保存地块信息
    const handleSave = async () => {
        // 验证所有地块名称都已填写
        const hasEmptyName = plotList.some(plot => !plot.plotName.trim());
        if (hasEmptyName) {
            message.error('请填写所有地块名称');
            return;
        }
        // 验证所有占比总和是否为1
        const totalPercentage = plotList.reduce((sum, plot) => sum + plot.percentage, 0);
        if (Math.abs(totalPercentage - 1) > 0.01) {
            message.error('所有地块占比总和必须为1');
            return;
        }

        if (plotList.some(plot => !!plot.ID)) {
            onSaveData();
            return
        }
        modalApi.confirm({
            title: null,
            icon: null,
            content: <span className="text-[13px]">保存后不可删除，如需删除请联系plm运营。目前存在的所有工作包将会被移入第一个地块中，个别工作包如果不属于第一个地块需要在plm手动调整到对应的地块中</span>,
            okButtonProps: {
                danger: true,
                type: "primary"
            },
            onOk() {
                onSaveData();
            }
        });
    };

    return (
        <Modal 
            title="设置地块" 
            width="fit-content" 
            destroyOnHidden 
            style={{ minWidth: 500 }}
            open={visible}
            onCancel={() => onClose()}
            footer={[
                <Button key="cancel" onClick={() => onClose()}>取消</Button>,
                <Button key="save" type="primary" loading={loading} onClick={handleSave}>保存</Button>
            ]}
        >
            <div>
                {modalContextHolder}
                <div className='flex items-center text-xs'>
                    <div className='flex-1'>地块名称</div>
                    <div className='w-[100px] shrink-0'>占比</div>
                    <div className='w-[40px] shrink-0 flex items-center justify-center'>操作</div>
                </div>
                {plotList.map((plot, index) => (
                    <div key={index} className='flex py-1'>
                        <div className='flex-1 pr-2'>
                            <Input className='w-full'
                                placeholder="地块名称"
                                value={plot.plotName}
                                onChange={(e) => handlePlotNameChange(index, e.target.value)}
                            />
                        </div>
                        <div className='w-[100px] shrink-0 flex items-center justify-center'>
                            <Input
                                className='w-full'
                                type="number"
                                placeholder="占比"
                                step={0.1}
                                value={plot.percentage}
                                onChange={(e) => {
                                    onPercentageChange(index, Number(e.target.value));
                                }}
                                style={{ width: '100%' }}
                            />
                        </div>
                        

                        <div className='w-[40px] flex items-center justify-center'>
                            {(index > 1 && !plot.ID) && (
                                <MinusCircleOutlined
                                    onClick={() => removePlot(index)}
                                    style={{ color: '#ff4d4f' }}
                                />
                            )}
                            
                        </div>
                    </div>
                ))}
                <Button
                    type="dashed"
                    onClick={addPlot}
                    icon={<PlusOutlined />}
                    style={{ width: '100%' }}
                    className='mt-2'
                >
                    添加地块
                </Button>
            </div>
        </Modal>
    )
}